// Town data for MAD Title Agency LLC local SEO optimization
//
// Market figures (medianHomePrice / marketTrend) are refreshed from
// marketData.json by scripts/market-data/refresh-market-data.js (Zillow ZHVI,
// city-level). The literals below are the fallback used when a town is missing
// from the JSON (e.g. Washington Borough, which the Zillow city CSV does not
// carry). See scripts/market-data/README.md.
import marketData from './marketData.json';

export interface TownData {
  slug: string;
  name: string;
  county: string;
  state: string;
  zipCodes: string[];
  population: string;
  medianHomePrice: string;
  marketTrend: string;
  description: string;
  keyNeighborhoods: string[];
  notableFeatures: string[];
  schoolDistricts: string[];
  testimonial: {
    quote: string;
    client: string;
    propertyType: string;
  };
  faq: Array<{
    question: string;
    answer: string;
  }>;
  serviceHighlights: string[];
  metaTitle: string;
  metaDescription: string;
  featured: boolean;
}

export const townsByCounty = {
  bergen: [
    {
      slug: 'ridgewood',
      name: 'Ridgewood',
      county: 'Bergen County',
      state: 'NJ',
      zipCodes: ['07450', '07451'],
      population: '25,979',
      medianHomePrice: '$1,000,000',
      marketTrend: '+3.1% YoY',
      description: 'Ridgewood is one of Bergen County\'s most prestigious communities, known for its excellent schools, tree-lined streets, and vibrant downtown. This affluent suburb offers a perfect blend of small-town charm and modern amenities.',
      keyNeighborhoods: ['Central Ridgewood', 'Graydon Park', 'Willard Elementary District', 'Heights District'],
      notableFeatures: ['Top-rated school district', 'Historic downtown village', 'Close to NYC transit', 'Family-friendly community'],
      schoolDistricts: ['Ridgewood Village School District'],
      testimonial: {
        quote: 'MAD Title made our Ridgewood home purchase seamless. Their local expertise was invaluable for navigating the competitive market.',
        client: 'Sarah & Mike Chen',
        propertyType: 'Single-family home'
      },
      faq: [
        {
          question: 'What makes Ridgewood real estate unique?',
          answer: 'Ridgewood features some of Bergen County\'s most sought-after properties, with excellent schools, historic architecture, and strong property values. The market is competitive with homes often selling quickly.'
        },
        {
          question: 'How long do title searches take in Ridgewood?',
          answer: 'Standard title searches in Ridgewood typically take 2-3 business days. Our local expertise helps expedite the process for time-sensitive transactions.'
        }
      ],
      serviceHighlights: ['Luxury Property Expertise', 'School District Knowledge', 'Fast Local Processing', 'Competitive Market Navigation'],
      metaTitle: 'Title Company Ridgewood NJ | Expert Real Estate Closings | MAD Title',
      metaDescription: 'Trusted title company in Ridgewood, NJ. Expert title insurance and closings for luxury homes in Bergen County. Local expertise, fast service.',
      featured: true
    },
    {
      slug: 'fort-lee',
      name: 'Fort Lee',
      county: 'Bergen County',
      state: 'NJ',
      zipCodes: ['07024'],
      population: '37,843',
      medianHomePrice: '$425,000',
      marketTrend: 'Stable YoY',
      description: 'Fort Lee offers stunning views of the Hudson River and Manhattan skyline, featuring luxury high-rise condominiums and convenient access to New York City via the George Washington Bridge.',
      keyNeighborhoods: ['Palisades', 'GWB Plaza District', 'Main Street Corridor', 'Bluff Area'],
      notableFeatures: ['Manhattan skyline views', 'Luxury condominiums', 'George Washington Bridge access', 'Diverse dining scene'],
      schoolDistricts: ['Fort Lee School District'],
      testimonial: {
        quote: 'The condo closing process was complex, but MAD Title handled every detail perfectly. Their Fort Lee experience really showed.',
        client: 'David Rodriguez',
        propertyType: 'Luxury condominium'
      },
      faq: [
        {
          question: 'Are there special considerations for Fort Lee condo purchases?',
          answer: 'Yes, Fort Lee condominiums often involve complex board approvals, HOA reviews, and building-specific requirements. Our team has extensive experience with these processes.'
        }
      ],
      serviceHighlights: ['Condominium Expertise', 'HOA Documentation', 'International Buyers', 'Manhattan Commuter Properties'],
      metaTitle: 'Fort Lee NJ Title Company | Condo Closings & Real Estate | MAD Title',
      metaDescription: 'Professional title services in Fort Lee, NJ. Specializing in luxury condominiums and high-rise properties near George Washington Bridge.',
      featured: true
    },
    {
      slug: 'englewood',
      name: 'Englewood',
      county: 'Bergen County',
      state: 'NJ',
      zipCodes: ['07631'],
      population: '29,308',
      medianHomePrice: '$680,000',
      marketTrend: '+5.7% YoY',
      description: 'Englewood combines suburban elegance with urban sophistication, featuring diverse architecture, excellent transportation, and a thriving business district.',
      keyNeighborhoods: ['Englewood Cliffs border', 'Central Business District', 'Residential hills', 'East Hill'],
      notableFeatures: ['Diverse community', 'Historic downtown', 'Medical facilities', 'Transportation hub'],
      schoolDistricts: ['Englewood Public School District'],
      testimonial: {
        quote: 'MAD Title understood the unique aspects of our Englewood property. Professional service from start to finish.',
        client: 'Maria Santos',
        propertyType: 'Colonial home'
      },
      faq: [
        {
          question: 'What property types are common in Englewood?',
          answer: 'Englewood features a mix of single-family homes, townhouses, and condominiums, ranging from historic properties to modern constructions.'
        }
      ],
      serviceHighlights: ['Historic Property Knowledge', 'Diverse Property Types', 'Transportation Access', 'Community Expertise'],
      metaTitle: 'Englewood NJ Title Company | Professional Real Estate Closings | MAD Title',
      metaDescription: 'Expert title services in Englewood, Bergen County NJ. Trusted for historic homes, modern properties, and complex transactions.',
      featured: true
    }
  ],
  middlesex: [
    {
      slug: 'edison',
      name: 'Edison',
      county: 'Middlesex County',
      state: 'NJ',
      zipCodes: ['08817', '08818', '08820', '08837'],
      population: '107,588',
      medianHomePrice: '$580,000',
      marketTrend: '+1.3% YoY',
      description: 'Edison is one of New Jersey\'s largest and most diverse communities, offering excellent schools, varied housing options, and strong economic opportunities in the heart of Central Jersey.',
      keyNeighborhoods: ['Menlo Park', 'Oak Tree', 'Clara Barton', 'Roosevelt Park', 'Bonhamtown'],
      notableFeatures: ['Top school districts', 'Cultural diversity', 'Corporate headquarters', 'Recreation facilities'],
      schoolDistricts: ['Edison Township School District'],
      testimonial: {
        quote: 'The MAD Title team made our Edison home purchase smooth and stress-free. Great local knowledge and service.',
        client: 'Priya & Raj Patel',
        propertyType: 'Single-family home'
      },
      faq: [
        {
          question: 'Why is Edison popular for families?',
          answer: 'Edison offers excellent schools, diverse housing options, recreational facilities, and easy access to major highways and train stations, making it ideal for families.'
        }
      ],
      serviceHighlights: ['School District Expertise', 'Family Home Specialists', 'Corporate Relocations', 'First-Time Buyers'],
      metaTitle: 'Edison NJ Title Company | Family Home Closings | MAD Title Agency',
      metaDescription: 'Trusted title company in Edison, NJ. Expert closings for family homes, corporate relocations, and first-time buyers in Middlesex County.',
      featured: true
    },
    {
      slug: 'woodbridge',
      name: 'Woodbridge',
      county: 'Middlesex County',
      state: 'NJ',
      zipCodes: ['07095', '07001', '08830'],
      population: '106,000',
      medianHomePrice: '$495,000',
      marketTrend: 'Stable YoY',
      description: 'Home to MAD Title Agency LLC headquarters, Woodbridge offers rich history, diverse neighborhoods, and excellent value in the Central Jersey real estate market.',
      keyNeighborhoods: ['Avenel', 'Colonia', 'Fords', 'Iselin', 'Port Reading', 'Sewaren'],
      notableFeatures: ['Historic communities', 'Diverse housing stock', 'Transportation access', 'Local businesses'],
      schoolDistricts: ['Woodbridge Township School District'],
      testimonial: {
        quote: 'Being local to Woodbridge, MAD Title knows every neighborhood. They guided us to the perfect home with expert service.',
        client: 'Lisa & Tom Johnson',
        propertyType: 'Colonial home'
      },
      faq: [
        {
          question: 'What are the benefits of working with a local Woodbridge title company?',
          answer: 'Our Woodbridge headquarters means deep local knowledge, established relationships with area professionals, and personalized service for your transaction.'
        }
      ],
      serviceHighlights: ['Local Headquarters', 'Neighborhood Expertise', 'Established Relationships', 'Personalized Service'],
      metaTitle: 'Woodbridge NJ Title Company | Local Real Estate Experts | MAD Title',
      metaDescription: 'Local title company in Woodbridge, NJ. Our headquarters location provides unmatched local expertise for your real estate closing.',
      featured: true
    },
    {
      slug: 'perth-amboy',
      name: 'Perth Amboy',
      county: 'Middlesex County',
      state: 'NJ',
      zipCodes: ['08861', '08862'],
      population: '55,436',
      medianHomePrice: '$490,000',
      marketTrend: '+2.6% YoY',
      description: 'Perth Amboy is a vibrant waterfront city with rich colonial history, diverse communities, and growing real estate opportunities along the Raritan Bay.',
      keyNeighborhoods: ['Historic Downtown', 'Waterfront District', 'Amboy Avenue Corridor', 'Staten Island Sound area'],
      notableFeatures: ['Waterfront properties', 'Historic downtown', 'Ferry access to Staten Island', 'Cultural diversity'],
      schoolDistricts: ['Perth Amboy Public Schools'],
      testimonial: {
        quote: 'MAD Title helped us navigate the unique aspects of our Perth Amboy waterfront property purchase. Excellent local knowledge.',
        client: 'Carlos & Maria Rodriguez',
        propertyType: 'Waterfront condominium'
      },
      faq: [
        {
          question: 'What makes Perth Amboy real estate unique?',
          answer: 'Perth Amboy offers waterfront living with historic charm, ferry access to Staten Island, and growing investment opportunities in an affordable market.'
        }
      ],
      serviceHighlights: ['Waterfront Properties', 'Historic District Knowledge', 'Investment Properties', 'Ferry Area Expertise'],
      metaTitle: 'Perth Amboy NJ Title Company | Waterfront Real Estate | MAD Title',
      metaDescription: 'Professional title services in Perth Amboy, NJ. Specializing in waterfront properties and historic downtown real estate transactions.',
      featured: false
    },
    {
      slug: 'carteret',
      name: 'Carteret',
      county: 'Middlesex County',
      state: 'NJ',
      zipCodes: ['07008'],
      population: '25,326',
      medianHomePrice: '$520,000',
      marketTrend: '+2.4% YoY',
      description: 'Carteret is a growing borough with diverse housing options, excellent transportation access, and strong community ties in eastern Middlesex County.',
      keyNeighborhoods: ['Roosevelt Avenue', 'Chrome section', 'Port District', 'Carteret Park area'],
      notableFeatures: ['Transportation hub', 'Industrial heritage', 'Diverse community', 'Affordable housing'],
      schoolDistricts: ['Carteret School District'],
      testimonial: {
        quote: 'Our Carteret home closing was handled professionally and efficiently. MAD Title made the process stress-free.',
        client: 'James & Patricia Kim',
        propertyType: 'Single-family home'
      },
      faq: [
        {
          question: 'Why is Carteret growing in popularity?',
          answer: 'Carteret offers affordable housing, excellent transportation access to NYC, and a strong sense of community, making it attractive to families and commuters.'
        }
      ],
      serviceHighlights: ['First-Time Buyers', 'Transportation Access', 'Community Knowledge', 'Affordable Markets'],
      metaTitle: 'Carteret NJ Title Company | Affordable Home Closings | MAD Title',
      metaDescription: 'Expert title services in Carteret, NJ. Specializing in affordable housing markets and first-time buyer transactions in Middlesex County.',
      featured: false
    },
    {
      slug: 'new-brunswick',
      name: 'New Brunswick',
      county: 'Middlesex County',
      state: 'NJ',
      zipCodes: ['08901', '08902', '08903'],
      population: '55,181',
      medianHomePrice: '$485,000',
      marketTrend: 'Stable YoY',
      description: 'New Brunswick is a vibrant college town and cultural hub, home to Rutgers University, with diverse housing options and a dynamic real estate market.',
      keyNeighborhoods: ['Downtown', 'University area', 'Highland Park border', 'French Street corridor'],
      notableFeatures: ['Rutgers University', 'Cultural district', 'Medical hub', 'Student housing'],
      schoolDistricts: ['New Brunswick Public Schools'],
      testimonial: {
        quote: 'The investment property closing was complex, but MAD Title handled all the details perfectly. Great service for our New Brunswick purchase.',
        client: 'Dr. Jennifer Chen',
        propertyType: 'Investment property'
      },
      faq: [
        {
          question: 'What types of properties are common in New Brunswick?',
          answer: 'New Brunswick features student housing, investment properties, condominiums, and single-family homes, with opportunities ranging from university area rentals to downtown developments.'
        }
      ],
      serviceHighlights: ['Investment Properties', 'University Area', 'Student Housing', 'Medical District'],
      metaTitle: 'New Brunswick NJ Title Company | Investment Property Experts | MAD Title',
      metaDescription: 'Specialized title services in New Brunswick, NJ. Expert handling of investment properties, student housing, and university area transactions.',
      featured: false
    }
  ],
  essex: [
    {
      slug: 'montclair',
      name: 'Montclair',
      county: 'Essex County',
      state: 'NJ',
      zipCodes: ['07042', '07043'],
      population: '39,701',
      medianHomePrice: '$1,100,000',
      marketTrend: '+3.2% YoY',
      description: 'Montclair is a vibrant arts community with excellent schools, diverse architecture, and easy NYC access, making it a premier destination for families and professionals.',
      keyNeighborhoods: ['Upper Montclair', 'Montclair Heights', 'Lower Montclair', 'Brookdale', 'Watchung Plaza'],
      notableFeatures: ['Arts and culture scene', 'Historic architecture', 'NYC train access', 'Top-rated schools'],
      schoolDistricts: ['Montclair Public Schools'],
      testimonial: {
        quote: 'MAD Title understood the unique character of Montclair\'s historic properties. Excellent service throughout our closing.',
        client: 'Jennifer & Mark Williams',
        propertyType: 'Victorian home'
      },
      faq: [
        {
          question: 'What makes Montclair properties special?',
          answer: 'Montclair features diverse architectural styles from Victorian to modern, with many historic properties requiring specialized knowledge for successful transactions.'
        }
      ],
      serviceHighlights: ['Historic Property Expertise', 'Arts Community Knowledge', 'Architecture Specialists', 'NYC Commuter Properties'],
      metaTitle: 'Montclair NJ Title Company | Historic Home Experts | MAD Title',
      metaDescription: 'Specialized title services in Montclair, Essex County NJ. Expert handling of historic properties and unique architectural homes.',
      featured: true
    }
  ],
  union: [
    {
      slug: 'rahway',
      name: 'Rahway',
      county: 'Union County',
      state: 'NJ',
      zipCodes: ['07065'],
      population: '29,691',
      medianHomePrice: '$525,000',
      marketTrend: '+7.5% YoY',
      description: 'Rahway is a historic city with a revitalized downtown, excellent train access to NYC, and diverse housing options from Victorian homes to modern developments.',
      keyNeighborhoods: ['Historic Downtown', 'Westfield Avenue corridor', 'Rahway River area', 'Seminary Avenue'],
      notableFeatures: ['Historic downtown revival', 'Direct NYC train access', 'Arts district', 'Diverse architecture'],
      schoolDistricts: ['Rahway Public Schools'],
      testimonial: {
        quote: 'MAD Title understood the unique character of our historic Rahway home. Their expertise was invaluable throughout the process.',
        client: 'Michael & Sarah Thompson',
        propertyType: 'Victorian home'
      },
      faq: [
        {
          question: 'What attracts buyers to Rahway?',
          answer: 'Rahway offers historic charm, direct train access to NYC, a thriving arts scene, and more affordable pricing compared to nearby markets.'
        }
      ],
      serviceHighlights: ['Historic Properties', 'NYC Commuter Access', 'Arts District', 'Revitalization Areas'],
      metaTitle: 'Rahway NJ Title Company | Historic Home Specialists | MAD Title',
      metaDescription: 'Professional title services in Rahway, Union County NJ. Expert handling of historic properties and downtown revitalization transactions.',
      featured: false
    }
  ],
  warren: [
    {
      slug: 'washington',
      name: 'Washington',
      county: 'Warren County',
      state: 'NJ',
      zipCodes: ['07882'],
      population: '6,461',
      medianHomePrice: '$300,000',
      marketTrend: 'Stable YoY',
      description: 'Washington Borough offers small-town charm with affordable housing, local shops, and convenient access throughout Warren County.',
      keyNeighborhoods: ['Downtown', 'West Washington', 'East Washington'],
      notableFeatures: ['Small-town community', 'Affordable housing', 'Historic downtown', 'Family-friendly'],
      schoolDistricts: ['Warren Hills Regional School District'],
      testimonial: {
        quote: 'MAD Title made our home purchase in Washington Borough smooth and stress-free. Excellent service from start to finish.',
        client: 'Amy & Chris Thompson',
        propertyType: 'Single-family home'
      },
      faq: [
        {
          question: 'Why choose Washington Borough for homeownership?',
          answer: 'Washington Borough offers excellent value with affordable homes, a charming downtown, and strong community feel while maintaining access to larger metro areas.'
        }
      ],
      serviceHighlights: ['Affordable Housing Experts', 'First-Time Buyers', 'Historic Properties', 'Rural Area Knowledge'],
      metaTitle: 'Washington Borough NJ Title Company | Affordable Home Closings | MAD Title',
      metaDescription: 'Professional title services in Washington Borough, Warren County NJ. Specializing in affordable homes and first-time buyer transactions.',
      featured: true
    }
  ]
};

// --- Overlay script-refreshed market data onto the town literals ----------
// Each town's medianHomePrice / marketTrend is derived from marketData.json at
// module load. A town absent from the JSON keeps its hardcoded literal above.
interface MarketTown {
  zhvi: number;
  yoyPct: number;
  asOf: string;
  regionId: number;
}

const formatPrice = (n: number): string => '$' + n.toLocaleString('en-US');
const formatTrend = (pct: number): string =>
  Math.abs(pct) < 1.5
    ? 'Stable YoY'
    : `${pct > 0 ? '+' : ''}${pct.toFixed(1)}% YoY`;

const marketTowns: Record<string, MarketTown> = (marketData as {
  towns: Record<string, MarketTown>;
}).towns;

(Object.keys(townsByCounty) as Array<keyof typeof townsByCounty>).forEach(
  (countyKey) => {
    townsByCounty[countyKey].forEach((town) => {
      const m = marketTowns[town.slug];
      if (m) {
        town.medianHomePrice = formatPrice(m.zhvi);
        town.marketTrend = formatTrend(m.yoyPct);
      }
    });
  }
);

export const getAllTowns = (): TownData[] => {
  return [
    ...townsByCounty.bergen,
    ...townsByCounty.middlesex,
    ...townsByCounty.essex,
    ...townsByCounty.union,
    ...townsByCounty.warren
  ];
};

export const getTownBySlug = (slug: string): TownData | undefined => {
  return getAllTowns().find(town => town.slug === slug);
};

export const getFeaturedTowns = (): TownData[] => {
  return getAllTowns().filter(town => town.featured);
}; 